import React, { useState } from 'react';
import { Button, Space, Menu, Dropdown, Tooltip, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    CloseOutlined,
    BellOutlined,
    UserSwitchOutlined,
    DeleteOutlined,
    EditOutlined,
    MoreOutlined,
    EyeInvisibleOutlined,
    FlagOutlined,
    AuditOutlined
} from '@ant-design/icons';
import { markConversationUnseen } from '../../../MessagesServices';
import ContactEditModal from '../../../../Shared/ContactEditModal/ContactEditModal';
import ReportUserModal from '../../../../Shared/ReportUserModal/ReportUserModal';

const MessageActions = ({
    screens,
    isIgnored,
    isSnoozed,
    isAdmin,
    updateContactStatus,
    selectedConversation,
    onToggleTickets,
    showTickets
}) => {
    const { t } = useTranslation();
    const [editModalVisible, setEditModalVisible] = useState(false);
    const {
        handleIgnore,
        handleSnooze,
        handleMarkAsAdmin,
        handleDelete,
        ignoreLoading,
        snoozeLoading,
        adminLoading,
        deleteLoading
    } = updateContactStatus;


    const handleMarkAsUnread = async () => {
        if (!selectedConversation?.id) return;
        try {
            // Use the last message id from conversation if present
            const lastId = selectedConversation?.last_message?.id;
            await markConversationUnseen(selectedConversation.id, lastId);
        } catch (e) { }
    };

    const [reportModalVisible, setReportModalVisible] = useState(false);


    const mobileActionsMenu = (
        <Menu>
            <Menu.Item
                key="ignore"
                icon={<CloseOutlined />}
                onClick={handleIgnore}
                disabled={ignoreLoading}
            >
                <div className="menu-item-content">
                    <span>{isIgnored ? t('unignore') : t('ignore')}</span>
                    {ignoreLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
                </div>
            </Menu.Item>

            <Menu.SubMenu
                key="snooze"
                icon={<BellOutlined />}
                title={
                    <div className="menu-item-content">
                        <span>{isSnoozed ? t('snoozed') : t('snooze')}</span>
                        {snoozeLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
                    </div>
                }
                disabled={snoozeLoading}
            >
                {isSnoozed ? (
                    <Menu.Item onClick={() => handleSnooze(0)}>{t('unsnooze')}</Menu.Item>
                ) : (
                    <>
                        <Menu.Item onClick={() => handleSnooze(300)}>{t('5_minutes')}</Menu.Item>
                        <Menu.Item onClick={() => handleSnooze(900)}>{t('15_minutes')}</Menu.Item>
                        <Menu.Item onClick={() => handleSnooze(1800)}>{t('30_minutes')}</Menu.Item>
                    </>
                )}
            </Menu.SubMenu>

            <Menu.Item
                key="mark-admin"
                icon={<UserSwitchOutlined />}
                onClick={handleMarkAsAdmin}
                disabled={adminLoading}
            >
                <div className="menu-item-content">
                    <span>{isAdmin ? t('unmark_as_admin') : t('mark_as_admin')}</span>
                    {adminLoading && <Spin size="small" style={{ marginInlineStart: 8 }} />}
                </div>
            </Menu.Item>

            <Menu.Item
                key="edit-contact"
                icon={<EditOutlined />}
                onClick={() => setEditModalVisible(true)}
            >
                {t('edit_contact')}
            </Menu.Item>

            <Menu.Item
                key="mark-unread"
                icon={<EyeInvisibleOutlined />}
                onClick={handleMarkAsUnread}
            >
                {t('mark_as_unread')}
            </Menu.Item>

            <Menu.Item
                key="report"
                icon={<FlagOutlined />}
                onClick={() => setReportModalVisible(true)}
            >
                {t('report_user')}
            </Menu.Item>

            <Menu.Item
                key="delete"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
            >
                {t('delete')}
                {deleteLoading && <Spin size="small" style={{ marginInlineStart: 8 }} />}
            </Menu.Item>
        </Menu>
    );

    const desktopSnoozeMenu = (
        <Menu>
            {isSnoozed ? (
                <Menu.Item onClick={() => handleSnooze(0)}>{t('unsnooze')}</Menu.Item>
            ) : (
                <>
                    <Menu.Item onClick={() => handleSnooze(300)}>{t('5_minutes')}</Menu.Item>
                    <Menu.Item onClick={() => handleSnooze(900)}>{t('15_minutes')}</Menu.Item>
                    <Menu.Item onClick={() => handleSnooze(1800)}>{t('30_minutes')}</Menu.Item>
                </>
            )}
        </Menu>
    );

    return (
        <>
            {!screens.md ? (
                <Space size={6}>
                    <Tooltip title={screens.xs ? null : (showTickets ? t('hide_tickets', 'Hide Tickets') : t('show_tickets', 'Show Tickets'))}>
                        <Button
                            icon={<AuditOutlined />}
                            onClick={onToggleTickets}
                            type={showTickets ? 'primary' : 'default'}
                            shape="circle"
                        />
                    </Tooltip>
                    <Dropdown overlay={mobileActionsMenu} trigger={['click']}>
                        <Button icon={<MoreOutlined />} shape="circle" style={{ flexShrink: 0 }} />
                    </Dropdown>
                </Space>
            ) : (
                <Space size={4} wrap={false} style={{ flexShrink: 1, minWidth: 0 }}>
                    <Tooltip title={isIgnored ? t('unignore_conversation') : t('ignore_conversation')}>
                        <Button
                            icon={<CloseOutlined />}
                            onClick={handleIgnore}
                            loading={ignoreLoading}
                            type={isIgnored ? 'primary' : 'default'}
                            danger={isIgnored}
                        />
                    </Tooltip>

                    <Tooltip title={isSnoozed ? t('snoozed') : t('snooze_conversation')}>
                        <span>
                            <Dropdown
                                overlay={desktopSnoozeMenu}
                                trigger={['click']}
                                disabled={snoozeLoading}
                            >
                                <Button
                                    icon={<BellOutlined />}
                                    loading={snoozeLoading}
                                    type={isSnoozed ? 'primary' : 'default'}
                                />
                            </Dropdown>
                        </span>
                    </Tooltip>

                    <Tooltip title={isAdmin ? t('unmark_as_admin') : t('mark_as_admin')}>
                        <Button
                            loading={adminLoading}
                            icon={<UserSwitchOutlined />}
                            onClick={handleMarkAsAdmin}
                            type={isAdmin ? 'primary' : 'default'}
                        />
                    </Tooltip>

                    <Tooltip title={t('edit_contact_information')}>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => setEditModalVisible(true)}
                        />
                    </Tooltip>

                    <Tooltip title={t('mark_as_unread')}>
                        <Button
                            icon={<EyeInvisibleOutlined />}
                            onClick={handleMarkAsUnread}
                        />
                    </Tooltip>

                    <Tooltip title={showTickets ? t('hide_tickets', 'Hide Tickets') : t('show_tickets', 'Show Tickets')}>
                        <Button
                            icon={<AuditOutlined />}
                            onClick={onToggleTickets}
                            type={showTickets ? 'primary' : 'default'}
                        />
                    </Tooltip>

                    <Tooltip title={t('report_user')}>
                        <Button
                            icon={<FlagOutlined />}
                            onClick={() => setReportModalVisible(true)}
                        />
                    </Tooltip>

                    <Tooltip title={t('delete_conversation')}>
                        <Button
                            danger
                            loading={deleteLoading}
                            icon={<DeleteOutlined />}
                            onClick={handleDelete}
                        />
                    </Tooltip>
                </Space>
            )}

            <ContactEditModal
                visible={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                contact={selectedConversation?.contact}
            />

            <ReportUserModal
                visible={reportModalVisible}
                onCancel={() => setReportModalVisible(false)}
                contactId={selectedConversation?.contact?.id}
            />
        </>
    );
};

export default MessageActions;
